import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useFormContext } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronUp } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "./ui/button";
import { useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Checkbox } from "./ui/checkbox";
import { PromptsAddInput } from "./PromptAddInput";
import { Label } from "./ui/label";
import { type ClusterWithPrompt } from "@/store/useDataStore";
import { Separator } from "@radix-ui/react-separator";
import { AnimatedNoboriLogoScanner } from "./AnimatedNoboriLogoScanner";

interface Step4 {
  error: string;
  brandId: string;
  isBrandAdd?: boolean;
  additionalAction?: () => void;
  loadingStep: boolean;
  clustersWithPrompts: ClusterWithPrompt[];
  isLoading: boolean;
  company: any;
  smallerLoading?: boolean;
  submitLabel?: string;
}

const defaultPrompts: ClusterWithPrompt[] = [];

export default function Step4({
  additionalAction,
  brandId,
  error,
  clustersWithPrompts,
  isLoading,
  company,
  isBrandAdd,
  loadingStep,
  submitLabel = "Continue",
  smallerLoading = false,
}: Step4) {
  const { t } = useLocale();
  const {
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useFormContext();
  const [promptError, setPromptError] = useState("");
  const [selectedPropmtsNumber, setSelectedPromptsNumber] = useState(0);
  const [progress, setProgress] = useState(50);
  const MAX_PROMPTS_NUMBER = 16;
  const queryClient = useQueryClient();
  const [lastClickedCluster, setLastClickedCluster] = useState("");

  const selectedClusters = watch("clusters") || [];
  const clustersKey = JSON.stringify(
    selectedClusters.map((cluster: { title: string }) => cluster.title),
  );

  useEffect(() => {
    if (!clustersWithPrompts.length) {
      return;
    }
    const selectedFiltered = clustersWithPrompts.map((cl) => {
      const generatedCountry = String(
        cl.prompts.find((item) => item.country)?.country || "",
      )
        .trim()
        .toUpperCase();
      const fallbackCountry = String(company?.country || "")
        .trim()
        .toUpperCase();
      const country = generatedCountry || fallbackCountry || "US";
      return {
        title: cl.title,
        clusterId: cl.clusterId,
        country,
        prompts: cl.prompts
          .filter((i) => i.selected)
          .map((item) => ({
            text: item.text,
            country: String(item.country || country)
              .trim()
              .toUpperCase(),
            isCustom: Boolean(item.isCustom),
          })),
      };
    });

    const selectedPropmts = selectedFiltered.reduce(
      (sum, section) => sum + section.prompts.length,
      0,
    );

    setSelectedPromptsNumber(selectedPropmts);

    setValue("clusters", selectedFiltered);
    // setAllClusterWithPropmts(selectedFiltered)
    setProgress((selectedPropmts * 100) / MAX_PROMPTS_NUMBER);
  }, [clustersWithPrompts, company, setValue]);

  useEffect(() => {
    setLastClickedCluster(clustersWithPrompts[0]?.title || "");
  }, []);

  const handlePromptsAdd = (pt: string, cluster: string) => {
    if (!pt.trim().length) {
      return setPromptError("Prompt name can not be empty");
    }
    if (selectedPropmtsNumber + 1 > MAX_PROMPTS_NUMBER) {
      return setPromptError(
        `You can add only up to ${MAX_PROMPTS_NUMBER} propmts.`,
      );
    }

    const currentData = clustersWithPrompts;
    const currentCluster: ClusterWithPrompt | undefined = currentData.find(
      (cl: any) => cl.title === cluster,
    );

    const promptNameExist = currentCluster?.prompts.some(
      (p: { text: string }) => p.text === pt,
    );

    if (promptNameExist) {
      return setPromptError("Prompt name already exists");
    }
    if (currentCluster) {
      const fallbackCountry = String(company?.country || "")
        .trim()
        .toUpperCase();
      const country =
        String(currentCluster.country || "")
          .trim()
          .toUpperCase() ||
        String(
          currentCluster.prompts.find((item) => item.country)?.country || "",
        )
          .trim()
          .toUpperCase() ||
        fallbackCountry ||
        "US";
      currentCluster.prompts = [
        ...currentCluster.prompts,
        { text: pt, selected: true, country, isCustom: true },
      ];
    }

    setSelectedPromptsNumber((prev) => prev + 1);

    queryClient.setQueryData(
      ["generatePrompts", brandId, clustersKey],
      currentData,
    );

    const selectedFiltered = clustersWithPrompts.map((cl) => {
      const generatedCountry = String(
        cl.prompts.find((item) => item.country)?.country || "",
      )
        .trim()
        .toUpperCase();
      const fallbackCountry = String(company?.country || "")
        .trim()
        .toUpperCase();
      const country = generatedCountry || fallbackCountry || "US";
      return {
        title: cl.title,
        clusterId: cl.clusterId,
        country,
        prompts: cl.prompts
          .filter((i) => i.selected)
          .map((item) => ({
            text: item.text,
            country: String(item.country || country)
              .trim()
              .toUpperCase(),
            isCustom: Boolean(item.isCustom),
          })),
      };
    });

    setValue("clusters", selectedFiltered);
    clearErrors("clusters");
  };

  return (
    <div className="flex flex-col">
      {additionalAction && (
        <div>
          <h2 className="text-xl font-semibold text-center mb-2">
            <LocalizedText id="legacy.select.your.prompts.1vywo6j" />
          </h2>

          <div className="text-center text-[#737373] text-sm mb-6">
            <LocalizedText id="legacy.choose.the.prompts.you.want.to.track.for.16fdb31" />
          </div>
        </div>
      )}
      <div className="mb-3">
        <div className="flex justify-between font-medium text-sm">
          {isLoading ? null : (
            <div className="flex items-center gap-6 w-full justify-between">
              <div className="text-xl font-semibold font-sans">
                <LocalizedText id="legacy.select.your.prompts.1vywo6j" />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-muted-foreground font-normal">
                  {selectedPropmtsNumber || 0}/{MAX_PROMPTS_NUMBER}
                </div>
                <div className="w-[100px]">
                  <Progress value={progress} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="relative pt-2 flex gap-3">
          <div className="flex flex-col gap-2 opacity-70 flex-grow">
            {Array.from({
              length: smallerLoading ? 4 : MAX_PROMPTS_NUMBER / 2,
            }).map((_, index) => {
              return (
                <div key={index} className="flex  items-center space-x-4">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-full bg-black/5" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-4 w-full rounded-full bg-black/5" />
                    <Skeleton className="h-4 w-[52%] rounded-full bg-black/5" />
                  </div>
                </div>
              );
            })}
          </div>
          {additionalAction && (
            <div
              className="absolute inset-0 grid place-items-center pointer-events-none"
              role="status"
              aria-label={t("legacy.generating.suggestions.1jlju1g")}
            >
              <div className="grid h-20 w-20 place-items-center rounded-2xl border border-border bg-black shadow-[0_12px_32px_rgba(0,0,0,0.12)]">
                <AnimatedNoboriLogoScanner
                  size={52}
                  intensity={1.15}
                  durationSec={1.15}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className={`flex flex-col gap-2 mt-3 ${isBrandAdd && "overflow-y-auto max-h-[188px]"}`}
          >
            {(clustersWithPrompts || defaultPrompts).map((pt: any, index) => {
              return (
                <Accordion
                  type="single"
                  key={index}
                  collapsible
                  className="w-full"
                  defaultValue="item-1"
                >
                  <AccordionItem
                    value={`item-${index + 1}`}
                    className="rounded-md border last:border-b-1"
                  >
                    <AccordionTrigger
                      showChevron={false}
                      onClick={(e) => {
                        const y = window.scrollY;

                        requestAnimationFrame(() => {
                          window.scrollTo({
                            top: y,
                            behavior: "smooth",
                          });
                        });

                        e.currentTarget.focus({ preventScroll: true });
                      }}
                      className="group cursor-pointer hover:no-underline p-3 bg-white"
                    >
                      <div className="flex justify-between w-full">
                        <div className="flex items-center gap-4">
                          <span className="cursor-pointer rounded-md ">
                            <ChevronUp
                              size={16}
                              className="transition-transform duration-500 group-data-[state=closed]:rotate-180"
                            />
                          </span>

                          <div className="text-[#0A0A0A] group-data-[state=closed]:text-[#545454]">
                            {pt.title}
                          </div>
                        </div>
                        <div className="text-sm font-normal text-[#0A0A0A] group-data-[state=closed]:text-[#737373]">
                          {pt.prompts.filter(
                            (i: { selected: boolean }) => i.selected,
                          ).length || 0}
                          /{pt.prompts.length || 0}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-2 flex flex-col gap-4 text-balance">
                      <div className="flex flex-col gap-2">
                        {pt.prompts.map((item: any, index: any) => {
                          return (
                            <Label
                              key={index}
                              className={`cursor-pointer flex items-center gap-2 p-2 rounded-md ${item.selected ? "bg-[#ffefe5]" : " bg-[#fafafa]"}`}
                            >
                              <div>
                                <Checkbox
                                  checked={item.selected}
                                  onCheckedChange={(value) => {
                                    setLastClickedCluster(pt.title);
                                    queryClient.setQueryData(
                                      ["generatePrompts", brandId, clustersKey],
                                      (oldData: any[] | undefined) => {
                                        if (!oldData) return oldData;
                                        if (
                                          value &&
                                          selectedPropmtsNumber + 1 >
                                            MAX_PROMPTS_NUMBER
                                        ) {
                                          return setPromptError(
                                            `You can select only up to ${MAX_PROMPTS_NUMBER} prompts.`,
                                          );
                                        } else if (
                                          !value &&
                                          selectedPropmtsNumber - 1 === 0
                                        ) {
                                          setPromptError(
                                            "All clusters should have at least 1 prompt selected.",
                                          );
                                        } else {
                                          setPromptError("");
                                        }

                                        return oldData.map((cluster) => {
                                          if (cluster.title !== pt.title)
                                            return cluster;

                                          return {
                                            ...cluster,
                                            prompts: cluster.prompts.map(
                                              (itm: {
                                                text: string;
                                                selected: boolean;
                                              }) =>
                                                itm.text === item.text
                                                  ? {
                                                      ...itm,
                                                      selected: !itm.selected,
                                                    }
                                                  : itm,
                                            ),
                                          };
                                        });
                                      },
                                    );
                                  }}
                                  className="bg-white mt-1 cursor-pointer data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 data-[state=checked]:text-white"
                                />
                              </div>
                              <div>
                                <div
                                  className={`font-medium min-w-0 whitespace-normal break-normal [text-wrap:wrap] leading-5`}
                                >
                                  {item.text}
                                </div>
                              </div>
                            </Label>
                          );
                        })}
                        <Separator className="my-1 h-px w-full bg-border" />
                        <PromptsAddInput
                          handlePromptsAdd={(pr) => {
                            setLastClickedCluster(pt.title);
                            handlePromptsAdd(pr, pt.title);
                          }}
                          setPromptError={setPromptError}
                        ></PromptsAddInput>
                        {(promptError || error) &&
                          lastClickedCluster === pt.title && (
                            <p className="text-red-500 text-sm mt-2">
                              {promptError || error}
                            </p>
                          )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              );
            })}
          </div>
          {errors.clusters && (
            <p className="text-red-500 text-sm mt-2">
              {(errors.clusters as FieldError)?.message}
            </p>
          )}
          <div className={`flex mt-5 ${additionalAction && "justify-between"}`}>
            {additionalAction ? (
              <Button
                type="button"
                className="cursor-pointer"
                onClick={() => {
                  additionalAction();
                  clearErrors();
                }}
                variant="outline"
              >
                <LocalizedText id="legacy.cancel.18x3nqb" />
              </Button>
            ) : null}
            <div
              className={`${additionalAction ? "" : "w-full"} flex justify-center`}
            >
              <Button
                type="submit"
                disabled={loadingStep}
                className={`flex cursor-pointer ${additionalAction ? "w-fit" : "w-fit py-[22px] px-[45px]"} hover:bg-[#ff8138] hover:text-white text-white bg-[#FF5E00]`}
              >
                {loadingStep ? (
                  <div className=" mr-2 flex justify-center items-center">
                    <Spinner variant="circle-filled" size={16}></Spinner>
                  </div>
                ) : null}
                {submitLabel}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
